import { commandsCtx, editorViewCtx } from '@milkdown/core';
import { emphasisSchema, strongSchema, linkSchema } from '@milkdown/preset-commonmark';
const NORMAL_TEXT = 0;
export function createClickFunction(getEditorInstance, isLoading, commandKey, payload) {
    return function () {
        if (isLoading) {
            return;
        }
        const editor = getEditorInstance();
        editor.action(ctx => {
            const commandManager = ctx.get(commandsCtx);
            ctx.get(editorViewCtx).focus();
            return commandManager.call(commandKey, payload);
        });
    };
}
export function detectEmphasisFormattingInSelection(ctx, selection) {
    // A slice which contains the editor view instance.
    const view = ctx.get(editorViewCtx);
    if (view.state == null) {
        return {
            isBoldActive: false,
            isItalicActive: false,
        };
    }
    // Node that represents the current document.
    const { doc } = view.state;
    /* All available on base Selection class
     * from - number: The lower bound of the selection's main range.
     * to - number: The upper bound of the selection's main range.
     * empty - boolean: Indicates whether the selection contains any content.
     * $from -ResolvedPos: The resolved lower bound of the selection's main range.
     */
    const { from, to, empty, $from } = selection;
    if (empty) {
        // Tests whether there is a mark of this type in the given set.
        const isSchemaActive = isMarkInSet($from.marks());
        const isBoldActive = isSchemaActive(strongSchema.type(ctx));
        const isItalicActive = isSchemaActive(emphasisSchema.type(ctx));
        return {
            isBoldActive,
            isItalicActive,
        };
    }
    // Test whether a given mark or mark type occurs in this document between the two given positions.
    const isSchemaActive = doesRangeHaveMark(doc, from, to);
    const isBoldActive = isSchemaActive(strongSchema.type(ctx));
    const isItalicActive = isSchemaActive(emphasisSchema.type(ctx));
    return {
        isBoldActive,
        isItalicActive,
    };
}
const doesRangeHaveMark = (doc, from, to) => (markType) => doc.rangeHasMark(from, to, markType);
const isMarkInSet = (marks) => (markType) => !!markType.isInSet(marks);
export function detectLinkSelection(ctx, selection) {
    const view = ctx.get(editorViewCtx);
    if (view.state == null) {
        return {
            isSelectionLink: false,
            selectionRange: {
                from: 0,
                to: 0,
            },
            linkAttributes: {
                href: '',
            },
            text: '',
        };
    }
    const { doc } = view.state;
    const { empty, from, to, $from } = selection;
    const linkAttributes = getLinkAttributes(ctx, selection);
    if (empty) {
        const selectionDetails = detectLinkSelectionForEmptyNode($from, ctx, linkAttributes);
        return {
            ...selectionDetails,
            empty: true,
        };
    }
    else {
        const linkMarkType = linkSchema.type(ctx);
        const isSelectionLink = doesRangeHaveMark(doc, from, to)(linkMarkType);
        // Capture selected text for new links and set it as range
        let linkText = doc.textBetween(from, to);
        let linkRange = { from, to };
        if (isSelectionLink) {
            // For existing links, find exact boundaries
            linkRange = findLinkBoundaries(doc, from, to, linkMarkType);
            linkText = doc.textBetween(linkRange.from, linkRange.to);
        }
        return {
            isSelectionLink,
            text: linkText,
            selectionRange: linkRange,
            linkAttributes: {
                href: linkAttributes.href ?? '',
            },
        };
    }
}
function detectSelectionRange(resolvePos) {
    return {
        to: resolvePos.end(),
        from: resolvePos.start(),
    };
}
function detectLinkSelectionForEmptyNode($from, ctx, linkAttributes) {
    const linkMarkType = linkSchema.type(ctx);
    const isSelectionLink = isMarkInSet($from.marks())(linkMarkType);
    let text = '';
    let selectionRange = detectSelectionRange($from);
    if (isSelectionLink) {
        const linkMark = $from.marks().find(mark => mark.type === linkMarkType);
        if (linkMark) {
            const linkRange = findLinkBoundaries($from.doc, $from.start(), $from.end(), linkMarkType, linkMark.attrs.href);
            text = $from.doc.textBetween(linkRange.from, linkRange.to);
            selectionRange = linkRange;
        }
    }
    return {
        isSelectionLink,
        text,
        selectionRange,
        linkAttributes: {
            href: linkAttributes.href ?? '',
            title: linkAttributes.title ?? '',
        },
    };
}
// This scans the text nodes to find exact start and end of a link mark. Required to edit links correctly.
function findLinkBoundaries(doc, startPos, endPos, linkMarkType, targetHref) {
    let linkFrom = startPos;
    let linkTo = startPos;
    doc.nodesBetween(startPos, endPos, (node, nodePos) => {
        const hasMatchingLink = node.isText &&
            node.marks.some(mark => {
                if (mark.type !== linkMarkType)
                    return false;
                return !targetHref || mark.attrs.href === targetHref;
            });
        if (hasMatchingLink) {
            const nodeStart = nodePos;
            const nodeEnd = nodePos + node.nodeSize;
            if (linkFrom === startPos || nodeStart < linkFrom)
                linkFrom = nodeStart;
            linkTo = nodeEnd;
        }
    });
    return { from: linkFrom, to: linkTo };
}
function getLinkAttributes(ctx, selection) {
    const marks = selection.$from.marksAcross(selection.$from);
    const linkMark = marks?.find(mark => mark.type === linkSchema.type(ctx));
    return {
        href: linkMark?.attrs?.href,
        title: linkMark?.attrs?.title,
    };
}
/**
 ** Heading Utilities
 */
export function detectHeadingSelection(ctx, selection) {
    // A slice which contains the editor view instance.
    const view = ctx.get(editorViewCtx);
    // heading level 0 represents Normal Text
    let headingLevel = NORMAL_TEXT;
    if (view.state == null) {
        return headingLevel;
    }
    // Node that represents the current document.
    const { doc } = view.state;
    /* All available on base Selection class
     * from - number: The lower bound of the selection's main range.
     * to - number: The upper bound of the selection's main range.
     * empty - boolean: Indicates whether the selection contains any content.
     * $from -ResolvedPos: The resolved lower bound of the selection's main range.
     */
    const { from, empty, to, $from } = selection;
    if (empty) {
        headingLevel = detectHeadingSelectionForEmptyNode($from);
    }
    else {
        doc.nodesBetween(from, to, parent => {
            if (parent.type.name === 'heading') {
                headingLevel = parent.attrs.level;
                // Stop iterating if a heading is found
                return true;
            }
            return false;
        });
    }
    return headingLevel;
}
export function detectExceededCharacterLimit(note, maxCharacters) {
    const noteLength = note.length;
    return noteLength > maxCharacters;
}
export function detectIsSendNoteDisabled(note, readOnlyYn, exceededCharacterLimit) {
    const isHeadingPrefix = detectNoteOnlyContainsHeadingPrefix(note);
    const noteLength = note.length;
    const isNoteEmpty = noteLength === 0 || isHeadingPrefix;
    return readOnlyYn || isNoteEmpty || exceededCharacterLimit;
}
export function disableTextHeadingDropdown(ctx, selection) {
    return detectListSelection(ctx, selection);
}
function detectNoteOnlyContainsHeadingPrefix(note) {
    return note
        .trim()
        .split('')
        .every(char => char === '#');
}
function detectHeadingSelectionForEmptyNode($from) {
    const parent = $from.parent;
    if (parent.type.name === 'heading') {
        return parent.attrs.level;
    }
    return NORMAL_TEXT;
}
/**
 ** List Utilities
 */
export const listTypeNames = ['bullet_list', 'ordered_list'];
/**
 * Walks up the node tree to find the current list type
 * @param ctx - Editor context
 * @param selection - Optional selection, defaults to current view selection
 * @returns The list type name, "unknown" if not in a list or multiple list types are selected.
 */
export function detectSelectedListType(ctx, selection) {
    const view = ctx.get(editorViewCtx);
    const sel = selection ?? view.state.selection;
    // Default: not in a list
    let listType;
    if (view.state == null || sel == null) {
        return listType;
    }
    const { from, to, empty, $anchor } = sel;
    // Note: Root is depth 0, anything greater than 1 will be a richer node structure
    if (empty) {
        return detectListSelectionForEmptyNode($anchor);
    }
    else if (view.state.doc) {
        // doc = Node that represents the current document
        // Range selection - walk through nodes in range
        const listTypes = new Set();
        let foundBlockingNode = false;
        // NOTE: this currently does not work with nested lists due to
        // our detection failing to identify nested lists of the same type.
        // The experience today is that the toggle is DISABLED as
        // the list type returns "unknown".
        // TODO: work captured here: https://ftdr.atlassian.net/browse/ST-9164
        view.state.doc.nodesBetween(from, to, node => {
            if (!canInsertListByNode(node)) {
                foundBlockingNode = true;
                return false;
            }
            else if (isValidListType(node.type.name)) {
                listTypes.add(node.type.name);
            }
        });
        // we default to "unknown" or undefined
        // if there is more than 1 list within a range
        if (listTypes.size > 1 || foundBlockingNode) {
            return 'unknown';
        }
        // Single type found - return it
        if (listTypes.size === 1) {
            listType = Array.from(listTypes)[0];
        }
    }
    return listType;
}
/**
 * type validator for ListTypeName
 * @param typeName string to determine if ListTypeName
 * @returns true if a ListTypeName, false if not
 */
export function isValidListType(typeName) {
    return listTypeNames.includes(typeName);
}
/**
 * Checks if currently in a specific list type
 * @param ctx - Editor context
 * @param listType - Type of list to check for
 * @param selection - Optional selection, defaults to current view selection
 * @returns true if in list type
 */
export function isInListType(ctx, listType, selection) {
    return detectSelectedListType(ctx, selection) === listType;
}
/**
 * Checks if inserting a list is allowed
 * @param selection snapshot of selection object
 * @returns true if list insertion is allowed
 */
export function canInsertList(selection) {
    const { $anchor } = selection;
    // Check if we're in a heading - headings cannot contain lists
    if ($anchor.parent.type.name === 'heading') {
        return false;
    }
    // Default to allowing list insertion
    return true;
}
/**
 * Checks if inserting a list is allowed - by Node
 * @param node a ProseMirror Node
 * @returns true if list insertion is allowed
 */
export function canInsertListByNode(node) {
    // Check if we're in a heading - headings cannot contain lists
    if (node.type.name === 'heading') {
        return false;
    }
    // Default to allowing list insertion
    return true;
}
function detectListSelectionForEmptyNode($anchor) {
    // Cursor position - check parent node
    const { parent } = $anchor;
    // Check if cursor is at the start of a list
    if (isValidListType(parent.type.name)) {
        return parent.type.name;
        // Check if cursor is at the start of a list item
    }
    else if (parent.type.name === 'list_item') {
        // Fetch the list type from the attributes
        return parent.attrs.listType;
    }
    let { depth } = $anchor;
    // Cursor within non-list node, traverse the depth of the current node to find the parent list node
    while (depth > 0) {
        const node = $anchor.node(depth);
        if (isValidListType(node.type.name)) {
            return node.type.name;
        }
        depth--;
    }
    return undefined;
}
/**
 * Determines whether the current selection contains an ordered or unordered list
 * @param ctx - Editor context
 * @param selection - Optional selection, defaults to current view selection
 * @returns boolean: true if the selection contains a list and false if it does not
 */
function detectListSelection(ctx, selection) {
    const { $anchor, empty, from, to } = selection;
    if (empty) {
        return detectListSelectionForEmptyNode($anchor) != null;
    }
    else {
        return detectListInPopulatedSelection(ctx, from, to);
    }
}
/**
 * Detects if a populated range selection contains a list
 * @param ctx - Editor context
 * @param from - number: The lower bound of the selection's main range.
 * @param to - number: The upper bound of the selection's main range.
 * @returns boolean: true if the selection contains a list and false if it does not
 */
function detectListInPopulatedSelection(ctx, from, to) {
    const view = ctx.get(editorViewCtx);
    let containsList = false;
    if (view.state == null) {
        return containsList;
    }
    const { doc } = view.state;
    doc.nodesBetween(from, to, parent => {
        if (isValidListType(parent.type.name)) {
            containsList = true;
            return;
        }
        else {
            return;
        }
    });
    return containsList;
}
export function enableDividerButtonYn(ctx, selection) {
    const { $from, $anchor } = selection;
    const linkAttributes = getLinkAttributes(ctx, selection);
    const { isSelectionLink } = detectLinkSelectionForEmptyNode($from, ctx, linkAttributes);
    const headingLevel = detectHeadingSelectionForEmptyNode($from);
    const activeList = detectListSelectionForEmptyNode($anchor);
    return (!isSelectionLink && selection.empty && headingLevel === NORMAL_TEXT && activeList == null);
}
//# sourceMappingURL=notes.js.map